import React from 'react';
import { Star } from 'lucide-react';
import { StatsDisplay } from './StatsDisplay';
import type { CompletedChallengeViewProps } from './types';
import styles from './CompletedChallengeView.module.css';

export function CompletedChallengeView({
  progress,
  challenge,
  frequency,
  scoreDescriptor,
  numberOfStars,
  stats,
  signatureColor,
}: CompletedChallengeViewProps) {
  // Format the challenge title based on frequency and completion status
  const getChallengeTitle = () => {
    switch (frequency) {
      case 'daily':
        return "Today's Puzzle - Completed!";
      case 'weekly':
        return "This Week's Puzzle - Completed!";
      case 'monthly':
        return "This Month's Puzzle - Completed!";
      default:
        return 'Challenge Completed!';
    }
  };

  // Render star icons based on stars earned
  const renderStars = () => {
    const earned = progress.starsEarned || 0;

    return (
      <div className={styles.starsContainer}>
        {Array.from({ length: numberOfStars }).map((_, index) => (
          <Star
            key={index}
            className={`${styles.star} ${index < earned ? styles.starEarned : styles.starEmpty}`}
            fill={index < earned ? 'currentColor' : 'none'}
          />
        ))}
      </div>
    );
  };

  // Format score - rely on backend completionText for game-specific formatting
  const formatScore = () => {
    if (progress.completionText) {
      return progress.completionText;
    }
    if (progress.score !== null && progress.score !== undefined) {
      return progress.score.toString();
    }
    return 'Completed';
  };

  // Format current streak
  const formatStreak = (streak?: number) => {
    if (streak === undefined || streak === null) return '0';
    return streak.toString();
  };

  // Get streak label based on frequency
  const getStreakLabel = () => {
    switch (frequency) {
      case 'daily':
        return 'Day Streak';
      case 'weekly':
        return 'Week Streak';
      case 'monthly':
        return 'Month Streak';
      default:
        return 'Current Streak';
    }
  };

  // Format success rate (API returns 0-100 percentage, see api-client.ts)
  const formatSuccessRate = (rate?: number) => {
    if (rate === undefined || rate === null) return 'N/A';
    return `${rate.toFixed(0)}%`;
  };

  return (
    <div className={styles.container}>
      {/* Completion Header with Stars */}
      <div className={styles.completionHeader} style={{ backgroundColor: 'var(--signature-color-soft)' }}>
        {renderStars()}
        <div className={styles.scoreText} style={{ color: signatureColor, WebkitTextFillColor: signatureColor }}>
          {formatScore()}
        </div>
      </div>

      {/* Challenge Info Section */}
      <div className={styles.challengeInfo} style={{ backgroundColor: 'var(--signature-color-soft)' }}>
        <h3 className={styles.challengeLabel}>
          {getChallengeTitle()}
        </h3>
        <div className={styles.puzzleTitle}>
          <div className={styles.puzzleName}>
            "{challenge.puzzleName}"
          </div>
        </div>
      </div>

      {/* Player Stats Grid - show relevant stats for completed view */}
      {stats && (
        <div className={styles.statsGrid}>
          <StatsDisplay
            value={formatStreak(stats.currentStreak)}
            label={getStreakLabel()}
            signatureColor={signatureColor}
          />
          <StatsDisplay
            value={stats.totalStars?.toString() || '0'}
            label="Total Stars"
            signatureColor={signatureColor}
          />
          <StatsDisplay
            value={formatSuccessRate(stats.successRate)}
            label="Success Rate"
            signatureColor={signatureColor}
          />
        </div>
      )}
    </div>
  );
}
